import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { AlertTriangle, Check, FileSearch, Loader2, X } from "lucide-react";
import axios from "axios";
import { API_URL, CONTACT_EMAIL } from "../config";
import { ButtonGhost, ButtonGold } from "./Buttons";

const SEGMENTOS = ["Clínica de Saúde / Estética", "Odontologia", "Serviços B2B", "Outro"];

const inputCls =
    "w-full border border-line bg-[#0a0a0a] px-4 py-3.5 text-sm text-white outline-none placeholder:text-[#555555] focus:border-gold transition-colors duration-300";
const labelCls = "mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-[#888888]";

const INITIAL = { nome: "", empresa: "", whatsapp: "", segmento: "" };

export default function LeadFormModal({ open, onClose }) {
    const [form, setForm] = useState(INITIAL);
    const [status, setStatus] = useState("idle");

    useEffect(() => {
        if (open) {
            window.__lenis?.stop();
            document.body.style.overflow = "hidden";
        } else {
            window.__lenis?.start();
            document.body.style.overflow = "";
        }
    }, [open]);

    useEffect(() => {
        const esc = (e) => {
            if (e.key === "Escape") onClose();
        };
        window.addEventListener("keydown", esc);
        return () => window.removeEventListener("keydown", esc);
    }, [onClose]);

    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const close = () => {
        onClose();
        setTimeout(() => {
            setStatus("idle");
            setForm(INITIAL);
        }, 350);
    };

    const submit = async (e) => {
        e.preventDefault();
        setStatus("sending");
        try {
            await axios.post(`${API_URL}/leads`, form);
            setStatus("done");
        } catch {
            setStatus("error");
        }
    };

    return (
        <AnimatePresence>
            {open && (
                <motion.div
                    key="lead-modal"
                    data-testid="lead-modal"
                    className="fixed inset-0 z-[200] flex items-center justify-center overflow-y-auto px-4 py-10"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                >
                    <div
                        className="fixed inset-0 bg-black/80 backdrop-blur-sm"
                        onClick={close}
                        data-testid="lead-modal-backdrop"
                    />
                    <motion.div
                        role="dialog"
                        aria-modal="true"
                        initial={{ opacity: 0, y: 32, scale: 0.98 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 24, scale: 0.98 }}
                        transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                        className="relative w-full max-w-md border border-gold-faint bg-[#141414] p-8 shadow-[0_40px_120px_rgba(0,0,0,0.8)] lg:p-10"
                        data-testid="lead-modal-panel"
                    >
                        <span className="absolute inset-x-0 top-0 h-[2px] bg-gold" />
                        <button
                            onClick={close}
                            data-testid="lead-form-close"
                            aria-label="Fechar formulário"
                            className="absolute right-4 top-4 p-2 text-[#888888] transition-colors duration-300 hover:text-gold"
                        >
                            <X size={18} />
                        </button>

                        {status === "done" ? (
                            <div className="py-8 text-center" data-testid="lead-form-success">
                                <span className="mx-auto flex h-14 w-14 items-center justify-center border border-gold">
                                    <Check size={22} className="text-gold" />
                                </span>
                                <h3 className="mt-7 font-serif text-2xl text-white">
                                    Esboço requisitado.
                                </h3>
                                <p className="mt-4 text-sm leading-relaxed text-[#9a9a9a]">
                                    Sua requisição foi registrada. O investigador entrará em
                                    contato via WhatsApp para iniciar a análise preliminar com
                                    base em dados públicos do seu negócio.
                                </p>
                                <ButtonGhost onClick={close} tid="lead-form-success-close" className="mt-9">
                                    Fechar
                                </ButtonGhost>
                            </div>
                        ) : (
                            <>
                                <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-gold">
                                    Dossiê Nº 001 — Solicitação
                                </p>
                                <h3 className="mt-4 pr-8 font-serif text-2xl leading-snug text-white">
                                    Solicitar Esboço Investigativo Gratuito
                                </h3>
                                <p className="mt-3 text-sm leading-relaxed text-[#888888]">
                                    A análise inicial é feita com base em informações públicas
                                    do seu negócio.
                                </p>

                                <form onSubmit={submit} className="mt-8 space-y-5">
                                    <div>
                                        <label htmlFor="lead-nome" className={labelCls}>
                                            Nome completo
                                        </label>
                                        <input
                                            id="lead-nome"
                                            required
                                            minLength={2}
                                            value={form.nome}
                                            onChange={set("nome")}
                                            placeholder="Dr. Claudio Gabriel"
                                            className={inputCls}
                                            data-testid="lead-form-input-nome"
                                        />
                                    </div>
                                    <div>
                                        <label htmlFor="lead-empresa" className={labelCls}>
                                            Empresa / Clínica
                                        </label>
                                        <input
                                            id="lead-empresa"
                                            required
                                            minLength={2}
                                            value={form.empresa}
                                            onChange={set("empresa")}
                                            placeholder="Clínica de Saúde & Estética"
                                            className={inputCls}
                                            data-testid="lead-form-input-empresa"
                                        />
                                    </div>
                                    <div>
                                        <label htmlFor="lead-whatsapp" className={labelCls}>
                                            WhatsApp de contato
                                        </label>
                                        <input
                                            id="lead-whatsapp"
                                            required
                                            minLength={8}
                                            value={form.whatsapp}
                                            onChange={set("whatsapp")}
                                            placeholder="(11) 99999-9999"
                                            className={inputCls}
                                            data-testid="lead-form-input-whatsapp"
                                        />
                                    </div>
                                    <div>
                                        <label htmlFor="lead-segmento" className={labelCls}>
                                            Segmento de atuação
                                        </label>
                                        <select
                                            id="lead-segmento"
                                            required
                                            value={form.segmento}
                                            onChange={set("segmento")}
                                            className={`${inputCls} appearance-none`}
                                            data-testid="lead-form-input-segmento"
                                        >
                                            <option value="" disabled>
                                                Selecione o segmento
                                            </option>
                                            {SEGMENTOS.map((s) => (
                                                <option key={s} value={s}>
                                                    {s}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    {status === "error" && (
                                        <p
                                            className="flex items-start gap-2 text-xs leading-relaxed text-[#b91c1c]"
                                            data-testid="lead-form-error"
                                        >
                                            <AlertTriangle size={13} className="mt-0.5 shrink-0" />
                                            Não foi possível registrar agora. Tente novamente,
                                            fale com o investigador pelo WhatsApp ou escreva
                                            para{" "}
                                            <a
                                                href={`mailto:${CONTACT_EMAIL}`}
                                                className="text-gold underline"
                                                data-testid="lead-form-error-email"
                                            >
                                                {CONTACT_EMAIL}
                                            </a>
                                        </p>
                                    )}

                                    <ButtonGold
                                        type="submit"
                                        tid="lead-form-submit-button"
                                        className="w-full"
                                    >
                                        {status === "sending" ? (
                                            <Loader2 size={15} className="animate-spin" />
                                        ) : (
                                            <FileSearch size={15} />
                                        )}
                                        {status === "sending" ? "Registrando..." : "Enviar requisição"}
                                    </ButtonGold>
                                </form>
                            </>
                        )}
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}
